import React, { useState, useEffect, useCallback } from 'react';
import { useCompany } from '../../../contexts/CompanyContext';
import { bonificacionesAPI } from '../../../api/bonificaciones';
import ListadoBonificaciones from './components/ListadoBonificaciones';
import DetalleBonificacion   from './components/DetalleBonificacion';
import CrearBonificacionModal from './components/CrearBonificacionModal';


export default function BonificacionesModule() {
  const { currentCompany } = useCompany();

  const [lista,          setLista]          = useState([]);
  const [selected,       setSelected]       = useState(null);
  const [detalle,        setDetalle]        = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [calculating,    setCalculating]    = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [activeTab,      setActiveTab]      = useState('resumen');
  const [error,          setError]          = useState(null);

  // ── Lista ──────────────────────────────────────────────────
  const loadLista = useCallback(async () => {
    if (!currentCompany?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await bonificacionesAPI.getAll(currentCompany.id);
      setLista(data.bonificaciones || []);
    } catch {
      setError('No se pudo cargar la lista de bonificaciones.');
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id]);

  useEffect(() => { loadLista(); }, [loadLista]);

  // ── Detalle cuando se selecciona ───────────────────────────
  useEffect(() => {
    if (!selected) { setDetalle([]); return; }
    setLoadingDetalle(true);
    bonificacionesAPI.getDetalle(selected.id)
      .then(data => setDetalle(data.detalle || []))
      .catch((err) => {
        console.error('[getDetalle] error:', err?.response?.status, err?.response?.data || err.message);
        setDetalle([]);
      })
      .finally(() => setLoadingDetalle(false));
  }, [selected]);

  // ── Calcular / crear ───────────────────────────────────────
  const handleCalcular = async (formData) => {
    setCalculating(true);
    setError(null);
    try {
      const data = await bonificacionesAPI.calcular({
        companyId: currentCompany.id,
        ...formData,
      });
      setShowModal(false);
      await loadLista();
      setSelected(data.bonificacion);
      setActiveTab('resumen');
    } catch (err) {
      const msg = err?.response?.data?.error || 'Error al calcular la bonificación.';
      setError(msg);
    } finally {
      setCalculating(false);
    }
  };

  // ── Calcular ISR ───────────────────────────────────────────
  const handleCalcularISR = async () => {
    try {
      const res = await bonificacionesAPI.calcularISR(selected.id);
      setSelected(res.bonificacion);
      setDetalle(res.detalle || []);
      setLista(prev => prev.map(b => b.id === res.bonificacion.id ? res.bonificacion : b));
    } catch (err) {
      alert('Error al calcular ISR: ' + (err?.response?.data?.error || err.message));
    }
  };

  // ── Aprobar ────────────────────────────────────────────────
  const handleAprobar = async () => {
    const periodo = selected.fecha_pago_prevista
      ? new Date(selected.fecha_pago_prevista + 'T00:00:00').toLocaleDateString('es-DO', { month: 'long', year: 'numeric' })
      : '';
    if (!window.confirm(
      `¿Aprobar "${selected.nombre}"?\n\n` +
      `ISR se registrará en: ${periodo}\n\n` +
      `Una vez aprobada no podrá editarse.`
    )) return;
    try {
      const data = await bonificacionesAPI.aprobar(selected.id);
      setSelected(data.bonificacion);
      setLista(prev => prev.map(b => b.id === data.bonificacion.id ? data.bonificacion : b));
    } catch (err) {
      setError('Error al aprobar: ' + (err?.response?.data?.error || err.message));
    }
  };

  // ── Marcar pagado (usa fecha_pago_prevista del servidor) ───
  const handlePagar = async () => {
    if (!window.confirm(`¿Confirmar pago de "${selected.nombre}"?\n\nSe registrará el ISR en el período indicado al aprobar.`)) return;
    try {
      const data = await bonificacionesAPI.pagar(selected.id);
      setSelected(data.bonificacion);
      setLista(prev => prev.map(b => b.id === data.bonificacion.id ? data.bonificacion : b));
    } catch (err) {
      setError('Error al marcar como pagado: ' + (err?.response?.data?.error || err.message));
    }
  };

  // ── Revertir a borrador (paso "Generado") para recalcular ─
  const handleEliminarNomina = async () => {
    if (!window.confirm(`¿Revertir "${selected.nombre}" al paso Generado?\nSe eliminará la nómina (si existe) y podrás recalcular ISR y hacer ajustes.`)) return;
    try {
      const data = await bonificacionesAPI.revertir(selected.id);
      setSelected(data.bonificacion);
      setLista(prev => prev.map(b => b.id === data.bonificacion.id ? data.bonificacion : b));
    } catch (err) {
      alert('Error al revertir: ' + (err?.response?.data?.error || err.message));
    }
  };

  // ── Eliminar (desde detalle) ───────────────────────────────
  const handleEliminar = async () => {
    if (!window.confirm(`¿Eliminar el borrador "${selected.nombre}"? Esta acción no puede deshacerse.`)) return;
    try {
      await bonificacionesAPI.eliminar(selected.id);
      setSelected(null);
      setDetalle([]);
      await loadLista();
    } catch (err) {
      alert('Error al eliminar: ' + (err?.response?.data?.error || err.message));
    }
  };

  // ── Eliminar (desde listado) ───────────────────────────────
  const handleEliminarFromList = async (bon) => {
    if (!window.confirm(`¿Eliminar el borrador "${bon.nombre}"? Esta acción no puede deshacerse.`)) return;
    try {
      await bonificacionesAPI.eliminar(bon.id);
      await loadLista();
    } catch (err) {
      alert('Error al eliminar: ' + (err?.response?.data?.error || err.message));
    }
  };

  // ── Actualizar detalle empleado ────────────────────────────
  const handleUpdateDetalle = async (detalleId, data) => {
    try {
      const res = await bonificacionesAPI.updateDetalle(selected.id, detalleId, data);
      setDetalle(prev => prev.map(d => d.id === detalleId ? res.detalle : d));
      // Refresh header totals
      const bon = await bonificacionesAPI.getById(selected.id);
      setSelected(bon.bonificacion);
      setLista(prev => prev.map(b => b.id === bon.bonificacion.id ? bon.bonificacion : b));
    } catch (err) {
      alert('Error al actualizar: ' + (err?.response?.data?.error || err.message));
    }
  };

  // ── Recalcular por desempeño ───────────────────────────────
  const handleRecalcularDesempeno = async () => {
    try {
      const res = await bonificacionesAPI.recalcularDesempeno(selected.id);
      setDetalle(res.detalle || []);
      setSelected(res.bonificacion);
      setLista(prev => prev.map(b => b.id === res.bonificacion.id ? res.bonificacion : b));
    } catch (err) {
      alert('Error al recalcular: ' + (err?.response?.data?.error || err.message));
    }
  };

  return (
    <div className="space-y-4">

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-4 text-red-500 hover:text-red-700 text-lg leading-none">&times;</button>
        </div>
      )}

      {selected ? (
        <DetalleBonificacion
          bonificacion={selected}
          detalle={detalle}
          tasaInfotep={parseFloat(selected.tasa_infotep || 0.005)}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onBack={() => { setSelected(null); setDetalle([]); }}
          onCalcularISR={handleCalcularISR}
          onAprobar={handleAprobar}
          onPagar={handlePagar}
          onEliminarNomina={handleEliminarNomina}
          onEliminar={handleEliminar}
          onUpdateDetalle={handleUpdateDetalle}
          onRecalcularDesempeno={handleRecalcularDesempeno}
          loading={loadingDetalle}
        />
      ) : (
        <ListadoBonificaciones
          lista={lista}
          onSelect={(b) => { setSelected(b); setActiveTab('resumen'); }}
          onCrear={() => setShowModal(true)}
          onEliminar={handleEliminarFromList}
          loading={loading}
        />
      )}

      {showModal && (
        <CrearBonificacionModal
          onClose={() => { if (!calculating) setShowModal(false); }}
          onConfirm={handleCalcular}
          calculating={calculating}
        />
      )}


    </div>
  );
}
